// src/pages/NotificationsPage.tsx
// Full notification center with filtering, search, and timeline view
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  CheckCircle,
  X,
  Filter,
  Search,
  Trash2,
  CheckCheck,
  ChevronDown,
  ExternalLink,
} from 'lucide-react';
import {
  useNotificationStore,
  NotificationType,
  NotificationPriority,
  NOTIFICATION_STYLES,
  type Notification,
} from '../stores/notificationStore';

const ALL_TYPES: NotificationType[] = [
  'task_created',
  'task_started',
  'task_progress',
  'task_completed',
  'task_failed',
  'task_cancelled',
  'phase_complete',
  'qa_audit',
  'deploy_started',
  'deploy_success',
  'deploy_failed',
  'error',
  'warning',
  'info',
  'system',
];

const ALL_PRIORITIES: NotificationPriority[] = ['low', 'normal', 'high', 'critical'];

const PRIORITY_COLORS: Record<NotificationPriority, string> = {
  low: 'text-gray-400 bg-gray-500/20',
  normal: 'text-blue-400 bg-blue-500/20',
  high: 'text-orange-400 bg-orange-500/20',
  critical: 'text-red-400 bg-red-500/20',
};

function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function groupByDate(notifications: Notification[]): Record<string, Notification[]> {
  const groups: Record<string, Notification[]> = {};
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();

  for (const notif of notifications) {
    const date = new Date(notif.timestamp).toDateString();
    let label = date;
    if (date === today) label = 'Today';
    else if (date === yesterday) label = 'Yesterday';

    if (!groups[label]) groups[label] = [];
    groups[label].push(notif);
  }

  return groups;
}

const NotificationCard: React.FC<{
  notification: Notification;
  onMarkRead: () => void;
  onDismiss: () => void;
}> = ({ notification, onMarkRead, onDismiss }) => {
  const style = NOTIFICATION_STYLES[notification.type];
  const priorityStyle = PRIORITY_COLORS[notification.priority];

  return (
    <div
      className={`relative p-4 rounded-lg border transition-all ${
        notification.read
          ? 'bg-[#0d1117]/40 border-[#30363d]/50'
          : `${style.bgColor} border-[#30363d]`
      }`}
      onClick={() => !notification.read && onMarkRead()}
    >
      {/* Unread indicator */}
      {!notification.read && (
        <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
      )}

      <div className="flex items-start gap-3">
        {/* Icon */}
        <span className="text-xl flex-shrink-0">{style.icon}</span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-medium ${notification.read ? 'text-gray-400' : 'text-white'}`}>
              {notification.title}
            </h3>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${priorityStyle}`}>
              {notification.priority}
            </span>
          </div>

          <p className={`text-sm ${notification.read ? 'text-gray-500' : 'text-gray-300'}`}>
            {notification.message}
          </p>

          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span>{formatRelativeTime(notification.timestamp)}</span>
            {notification.taskId && (
              <Link
                to={`/?task=${notification.taskId}`}
                className="flex items-center gap-1 text-cyan-400 hover:text-cyan-300"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="font-mono">{notification.taskId.slice(0, 8)}</span>
                <ExternalLink className="w-3 h-3" />
              </Link>
            )}
            {notification.phase && (
              <span className="text-purple-400">Phase: {notification.phase}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {!notification.read && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead();
              }}
              className="p-1.5 rounded text-gray-500 hover:text-green-400 hover:bg-green-500/10 transition"
              title="Mark as read"
            >
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            className="p-1.5 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterDropdown: React.FC<{
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition ${
          selected.length > 0
            ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400'
            : 'bg-[#21262d] border-[#30363d] text-gray-400 hover:text-white'
        }`}
      >
        <Filter className="w-4 h-4" />
        <span className="text-sm">{label}</span>
        {selected.length > 0 && (
          <span className="bg-cyan-500/20 text-cyan-300 text-xs px-1.5 py-0.5 rounded">
            {selected.length}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-48 bg-[#161b22] border border-[#30363d] rounded-lg shadow-xl z-50 py-1 max-h-64 overflow-y-auto">
            {options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => toggleOption(opt.value)}
                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-[#21262d] transition"
              >
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center ${
                    selected.includes(opt.value)
                      ? 'bg-cyan-500 border-cyan-500'
                      : 'border-gray-500'
                  }`}
                >
                  {selected.includes(opt.value) && <CheckCircle className="w-3 h-3 text-white" />}
                </div>
                <span className={selected.includes(opt.value) ? 'text-white' : 'text-gray-400'}>
                  {opt.label}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export function NotificationsPage() {
  const {
    notifications,
    unreadCount,
    filters,
    setFilters,
    markAsRead,
    markAllAsRead,
    dismiss,
    dismissAll,
    clearOld,
  } = useNotificationStore();

  const [searchQuery, setSearchQuery] = useState('');

  // Filter notifications
  const filtered = useMemo(() => {
    return notifications.filter((n) => {
      if (n.dismissed) return false;
      if (!filters.showRead && n.read) return false;
      if (filters.types.length > 0 && !filters.types.includes(n.type)) return false;
      if (filters.priorities.length > 0 && !filters.priorities.includes(n.priority)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          n.title.toLowerCase().includes(q) ||
          n.message.toLowerCase().includes(q) ||
          n.taskId?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [notifications, filters, searchQuery]);

  // Group by date
  const grouped = useMemo(() => groupByDate(filtered), [filtered]);
  const dateGroups = Object.keys(grouped);

  // Stats
  const stats = useMemo(() => {
    const active = notifications.filter((n) => !n.dismissed);
    return {
      total: active.length,
      unread: unreadCount,
      errors: active.filter((n) => n.type === 'task_failed' || n.type === 'deploy_failed' || n.type === 'error').length,
      deploys: active.filter((n) => n.type.startsWith('deploy')).length,
    };
  }, [notifications, unreadCount]);

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/30">
              <Bell className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Notifications</h1>
              <p className="text-gray-400 text-sm">
                {stats.unread} unread of {stats.total} notifications
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#21262d] border border-[#30363d] text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all read
            </button>
            <button
              onClick={dismissAll}
              disabled={stats.total === 0}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Clear all
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-[#161b22] rounded-xl border border-[#30363d] p-4">
            <div className="text-2xl font-bold text-cyan-400">{stats.total}</div>
            <div className="text-xs text-gray-500">Total Active</div>
          </div>
          <div className="bg-[#161b22] rounded-xl border border-[#30363d] p-4">
            <div className="text-2xl font-bold text-purple-400">{stats.unread}</div>
            <div className="text-xs text-gray-500">Unread</div>
          </div>
          <div className="bg-[#161b22] rounded-xl border border-[#30363d] p-4">
            <div className="text-2xl font-bold text-red-400">{stats.errors}</div>
            <div className="text-xs text-gray-500">Errors/Failures</div>
          </div>
          <div className="bg-[#161b22] rounded-xl border border-[#30363d] p-4">
            <div className="text-2xl font-bold text-green-400">{stats.deploys}</div>
            <div className="text-xs text-gray-500">Deployments</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#21262d] border border-[#30363d] rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:border-cyan-500 outline-none text-sm"
            />
          </div>

          {/* Type Filter */}
          <FilterDropdown
            label="Type"
            options={ALL_TYPES.map((t) => ({
              value: t,
              label: t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            }))}
            selected={filters.types}
            onChange={(types) => setFilters({ types: types as NotificationType[] })}
          />

          {/* Priority Filter */}
          <FilterDropdown
            label="Priority"
            options={ALL_PRIORITIES.map((p) => ({
              value: p,
              label: p.charAt(0).toUpperCase() + p.slice(1),
            }))}
            selected={filters.priorities}
            onChange={(priorities) => setFilters({ priorities: priorities as NotificationPriority[] })}
          />

          {/* Show Read Toggle */}
          <button
            onClick={() => setFilters({ showRead: !filters.showRead })}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition text-sm ${
              filters.showRead
                ? 'bg-[#21262d] border-[#30363d] text-gray-400'
                : 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400'
            }`}
          >
            <CheckCircle className="w-4 h-4" />
            {filters.showRead ? 'Showing all' : 'Hiding read'}
          </button>

          {/* Clear old */}
          <button
            onClick={() => clearOld(7)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#21262d] border border-[#30363d] text-gray-400 hover:text-white transition text-sm"
          >
            <Trash2 className="w-4 h-4" />
            Clear 7d+
          </button>
        </div>

        {/* Notification List */}
        {filtered.length === 0 ? (
          <div className="bg-[#161b22] rounded-xl border border-[#30363d] p-12 text-center">
            <Bell className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">No notifications</h3>
            <p className="text-gray-500 text-sm">
              {notifications.length > 0
                ? 'Try adjusting your filters'
                : 'Pipeline events will appear here'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {dateGroups.map((dateLabel) => (
              <div key={dateLabel}>
                <h2 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                  <span>{dateLabel}</span>
                  <span className="text-gray-600">({grouped[dateLabel].length})</span>
                </h2>
                <div className="space-y-2">
                  {grouped[dateLabel].map((notif) => (
                    <NotificationCard
                      key={notif.id}
                      notification={notif}
                      onMarkRead={() => markAsRead(notif.id)}
                      onDismiss={() => dismiss(notif.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Enable browser notifications prompt */}
        {'Notification' in window && Notification.permission === 'default' && (
          <div className="bg-[#161b22] rounded-xl border border-cyan-500/30 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-sm text-white">Enable browser notifications</p>
                <p className="text-xs text-gray-500">Get notified of critical events even when this tab is in background</p>
              </div>
            </div>
            <button
              onClick={() => Notification.requestPermission()}
              className="px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition text-sm font-medium"
            >
              Enable
            </button>
          </div>
        )}
      </div>
    </main>
  );
}

export default NotificationsPage;
